/**
 * Render quality presets (spec §17.3). Presets drive shadows, vegetation density, texture
 * resolution, LOD aggressiveness, and antialiasing. Higher-level systems read the
 * resolved {@link RenderQualitySettings} rather than branching on the enum directly.
 */
export enum RenderQuality {
  Low = 'low',
  Medium = 'medium',
  High = 'high',
  Presentation = 'presentation',
}

export interface RenderQualitySettings {
  /** Whether shadow maps are enabled. */
  readonly shadows: boolean;
  /** Shadow map resolution (px, square). 0 when shadows disabled. */
  readonly shadowMapSize: number;
  /** Relative vegetation density multiplier (0..1+). */
  readonly vegetationDensity: number;
  /** Target texture resolution class in pixels. */
  readonly textureResolution: number;
  /** LOD aggressiveness: higher swaps to cheaper LODs sooner. */
  readonly lodBias: number;
  /** Hardware antialiasing (MSAA) requested at context creation. */
  readonly antialias: boolean;
  /** Device pixel ratio cap to bound fill cost. */
  readonly maxPixelRatio: number;
  /** Distance (world meters) beyond which shadows are not cast. */
  readonly shadowDistance: number;
}

const PRESETS: Readonly<Record<RenderQuality, RenderQualitySettings>> = {
  [RenderQuality.Low]: {
    shadows: false,
    shadowMapSize: 0,
    vegetationDensity: 0.5,
    textureResolution: 512,
    lodBias: 1.5,
    antialias: false,
    maxPixelRatio: 1,
    shadowDistance: 40,
  },
  // The default preset, and so the one almost every frame is drawn at. Its shadow map was 1024
  // across 80 m — 8 cm per shadow texel, which is coarser than a leaf, so foliage shadows arrived as
  // soft grey patches with no structure in them. 2048 halves that and is the single change that most
  // improves what a garden looks like at rest; a shadow map is also cheap next to the fill cost of
  // the same pixels, which is why the pixel-ratio cap moves far less.
  [RenderQuality.Medium]: {
    shadows: true,
    shadowMapSize: 2048,
    vegetationDensity: 1,
    textureResolution: 1024,
    lodBias: 1,
    antialias: true,
    maxPixelRatio: 1.75,
    shadowDistance: 80,
  },
  [RenderQuality.High]: {
    shadows: true,
    shadowMapSize: 4096,
    vegetationDensity: 1.25,
    textureResolution: 2048,
    lodBias: 0.85,
    antialias: true,
    maxPixelRatio: 2,
    shadowDistance: 140,
  },
  [RenderQuality.Presentation]: {
    shadows: true,
    shadowMapSize: 4096,
    vegetationDensity: 1.5,
    textureResolution: 4096,
    lodBias: 0.6,
    antialias: true,
    maxPixelRatio: 2,
    shadowDistance: 220,
  },
};

/** Resolve a quality preset into its concrete settings. */
export function getQualitySettings(quality: RenderQuality): RenderQualitySettings {
  return PRESETS[quality];
}

/** All presets in ascending fidelity order (useful for UI selectors). */
export const RENDER_QUALITY_ORDER: readonly RenderQuality[] = [
  RenderQuality.Low,
  RenderQuality.Medium,
  RenderQuality.High,
  RenderQuality.Presentation,
];
